import { useContext } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Badge,
  Card,
  CardBody,
  CardFooter,
  CardText,
  CardTitle,
} from 'reactstrap';
import { getDashColor } from '../../../shared/components/icons';
import { CampaignSerializer, IFundraiserItem } from '../../../types/campaign';
import { CampaignContext } from '../../../context/campaign';

export interface UpcomingFundraisersCardProps {
  app_items: IFundraiserItem[];
}

export interface FundraisingStatusCardProps {
  app_item: CampaignSerializer;
}

export const UpcomingFundaisersCard = ({
  app_items,
}: UpcomingFundraisersCardProps) => {
  function showItems(items: IFundraiserItem[]) {
    const fundraisers = Object.values(items);
    const eventsUrl = import.meta.env.VITE_EVENT_URL;
    const wpUrl = import.meta.env.VITE_WP_URL || '';
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

    if (fundraisers.length > 0) {
      const upcomingFundraisers = fundraisers.filter((item: IFundraiserItem) => {
        if (!item.published) {
          return false;
        }

        if (item.is_ongoing) {
          return true;
        }

        // Exclude events that have explicitly ended
        if (item.event_end_date) {
          const endDate = new Date(item.event_end_date);
          if (endDate < today) {
            return false;
          }
        }

        // Show if the event start date is still in the future
        if (item.event_date) {
          const eventDate = new Date(item.event_date);
          return eventDate >= today;
        }

        // Fallback: use registration_close_date if no event_date is set
        if (item.registration_close_date) {
          const closeDate = new Date(item.registration_close_date);
          return closeDate >= today;
        }

        return false;
      });

      // Show the first 3 upcoming fundraisers
      const displayFundraisers = upcomingFundraisers.slice(0, 3);
      
      if (displayFundraisers.length === 0) {
        return (
          <>
            This campaign has no upcoming fundraisers. Click the 'Manage
            Fundraisers' button below to get started.
          </>
        );
      }
      
      return displayFundraisers.map((item: IFundraiserItem, i: number) => {
        // Format registration close date if it exists and is different from event dates
        let registrationNote = '';
        if (item.registration_close_date) {
          const closeDate = new Date(item.registration_close_date);
          const formattedCloseDate = closeDate.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          });
          
          // Only show registration close date if it's meaningful
          // (e.g., not the same as the end date)
          if (item.event_end_date) {
            const endDate = new Date(item.event_end_date);
            if (closeDate.getTime() !== endDate.getTime()) {
              registrationNote = `Register by ${formattedCloseDate}`;
            }
          } else {
            registrationNote = `Register by ${formattedCloseDate}`;
          }
        }
        
        return (
          <CardText
            key={String(i)}
            tag="div"
            className='border-bottom pb-3'
            style={{ fontSize: '14px' }}
          >
            <span style={{ display: 'block', fontSize: '12px' }}>
              {item.formatted_display_date}
            </span>
            <a href={item.wp_event_id ? `${wpUrl}/?p=${item.wp_event_id}` : `${eventsUrl}/event/${item.id}/signup/`} target='_blank'>
              <span className='text-success fw-bold'>{item.name}</span>
            </a>
            {registrationNote && (
              <span style={{ display: 'block', fontSize: '11px', color: '#666', marginTop: '4px' }}>
                {registrationNote}
              </span>
            )}
          </CardText>
        );
      });
    } else {
      return (
        <>
          This campaign has no upcoming fundraisers. Click the 'Manage
          Fundraisers' button below to get started.
        </>
      );
    }
  }

  return (
    <Card className='border h-100'>
      <CardBody>
        <CardTitle
          style={{
            fontSize: '26px',
            fontWeight: '900',
          }}
        >
          Upcoming Fundraisers
        </CardTitle>
        {showItems({ ...app_items })}
      </CardBody>
      <CardFooter>
        <NavLink to='/fundraisers' className='btn btn-outline-dark btn-sm'>
          Manage Fundraisers
        </NavLink>
      </CardFooter>
    </Card>
  );
};

export const FundraisingStatusCard = ({
  app_item,
}: FundraisingStatusCardProps) => {
  // NOTE: 1. Added display of status and funded_date for dev use
  //       2. Assume that if there is a funded_date then
  //          fundraising must be Enabled
  const { campaign_status, funded_date } = app_item;
  const [{ campaign }, campaignDispatch] = useContext(CampaignContext);

  const status = 'Status: ' + campaign_status;
  const funded_text = funded_date ? 'Enabled' : 'Not Enabled';

  const { campaignDetail } = campaign;

  const showStatuses = ['disbursable', 'approved', 'funded'];
  if (!showStatuses.includes(campaignDetail.campaign_status_code)) {
    return <></>;
  }

  return (
    <Card
      style={{
        height: '100%',
        color: 'white',
        background: getDashColor('fundraising'),
      }}
    >
      <CardBody>
        <CardTitle
          style={{
            fontSize: '26px',
            fontWeight: '900',
          }}
        >
          Start Fundraising
        </CardTitle>
        <CardText>some text here...</CardText>
        <Badge className='bg-white text-dark' pill>
          {funded_text}
        </Badge>
        <CardText style={{ fontSize: '14px' }}>{status}</CardText>
        <CardText style={{ fontSize: '14px' }}>
          Funded Date: {funded_date}
        </CardText>
      </CardBody>
    </Card>
  );
};
